import todoIcon from "@/assets/icons/todos.png";
import { ScrollArea } from "@/components/ui/scroll-area";
import ExpandableCard from "@/components/widgets/dashboard/ExpandableCard";
import TodoItem from "@/domain/entities/TodoItem";
import useGetDashboardTodos from "@/hooks/useGetDashboardTodos";
import useManageTodo from "@/hooks/useManageTodo";
import { queryKeys } from "@/lib/queryKeys";
import mailStore from "@/stores/mailStore";
import { useQueryClient } from "@tanstack/react-query";
import { shallow, useSelector } from "@tanstack/react-store";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardTodoLine from "./DashboardTodoLine";

type DashboardTodosProps = {
    isExpanded: boolean;
    onExpand: () => void;
    onCollapse: () => void;
};

const DashboardTodos = ({
    isExpanded,
    onExpand,
    onCollapse,
}: DashboardTodosProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { setTodoState } = useManageTodo();
    const { data: todos } = useGetDashboardTodos();
    // Checked todos stay listed until their fade-out ends.
    const [fadingOutIds, setFadingOutIds] = useState<ReadonlySet<string>>(
        new Set(),
    );

    const setFadingOut = (todoId: string, isFadingOut: boolean) =>
        setFadingOutIds((previous) => {
            const next = new Set(previous);
            if (isFadingOut) next.add(todoId);
            else next.delete(todoId);
            return next;
        });

    const openedMail = useSelector(mailStore, (state) => state.openedMail, {
        compare: shallow,
    });

    const handleCompletionToggle = async (todoToUpdate: TodoItem) => {
        const done = !todoToUpdate.done;
        setFadingOut(todoToUpdate.id, done);

        const success = await setTodoState(todoToUpdate, done);
        if (success) {
            queryClient.invalidateQueries({
                queryKey: queryKeys.mailThread.detail(
                    openedMail?.internetMessageId,
                ),
            });
        } else {
            setFadingOut(todoToUpdate.id, false);
        }
    };

    if (!todos) return null;

    const visibleTodos = todos.filter(
        (todo) => !todo.done || fadingOutIds.has(todo.id),
    );
    const pendingTodoCount = visibleTodos.filter((todo) => !todo.done).length;

    return (
        <ExpandableCard
            className="h-40 min-w-65 flex-1"
            icon={todoIcon}
            isExpanded={isExpanded}
            onCollapse={onCollapse}
            onExpand={onExpand}
            title={t("dashboard.widgets.todos", {
                count: pendingTodoCount,
            })}
        >
            <ScrollArea viewportClassName="h-full">
                <div className="flex flex-col gap-1 text-white">
                    {visibleTodos.map((todo) => (
                        <DashboardTodoLine
                            key={`${todo.id}-${todo.internetMessageId}`}
                            handleCompletionToggle={handleCompletionToggle}
                            onFadeOutEnd={() => setFadingOut(todo.id, false)}
                            todo={todo}
                        />
                    ))}
                </div>
            </ScrollArea>
        </ExpandableCard>
    );
};

export default DashboardTodos;
